<template>
  <span>
    <el-autocomplete
        :disabled="disabled"
        @clear="clearAll"
        @keyup.native="clearParentModel"
        v-alphanumeric-validation
        v-model="localModel"
        clearable
        size="mini"
        :placeholder="placeholder"
        :fetch-suggestions="querySearchAsync"
        :highlight-first-item="true"
        @select="handleSelect"
        :trigger-on-focus="false"
        :style="styles"
        :class="classCss || 'width-100p'"
        :maxlength="maxlength"
        :popper-append-to-body="popperAppendToBody"
        @keydown.tab.native="selectWithTab"
        popper-class="autocomplete-popper"
        ref="autocomplete-principal"
        >
    </el-autocomplete>
  </span>
</template>

<script>
import constants from '@/utils/constants';
import autocompleteApiService from '@/api/autocompleteService';

const updateModelEvent = 'update:model';
const updateTextEvent = 'update:text';
const autocompletePrincipalRefStr = 'autocomplete-principal';
export default {
  name: 'Autocomplete',
  data() {
    return {
      localModel: null,
      list: []
    };
  },
  props: {
    /* The model of parent that must be updated when user select option. */
    model: {
      default: null
    },
    /* Additional model of parent that must be updated when user select option with other value of item selected. */
    modelAdditional: {
      default: null
    },
    /* End Point Url to search data. */
    url: {
      type: String,
      default: null
    },
    /* Current ShipperAccountId. */
    shipperAccountId: {
      type: Number,
      default: null
    },
    /* filter by warehouseId. */
    warehouseId: {
      type: Number,
      default: null
    },
    includeInactive: {
      type: Boolean,
      default: null
    },
    includeOnHold: {
      type: Boolean,
      default: null
    },
    maxlength: {
      type: Number,
      default: 100
    },
    /* If the autocomplete item must be selected to change the parent model. */
    strict: {
      type: Boolean,
      default: true
    },
    /* The attribute name of item that must be show in the options of autocomplete. */
    labelField: {
      type: String,
      default: null
    },
    /* The attribute name of item that must be show in the options of autocomplete concatenated with dash after labelField. */
    labelFieldLastWithDash: {
      type: String,
      default: null
    },
    /* Optional: If the value that we need to show is diferent to labelField. */
    labelfieldSelected: {
      type: String,
      default: null
    },
    /* The attribute name of item that must be updated the model. */
    valueField: {
      type: String,
      default: null
    },
    /* The attribute name of item that must be updated the modelAdditional. */
    valueFieldAdditional: {
      type: String,
      default: null
    },
    styles: {
      type: String,
      default: null
    },
    classCss: {
      type: String,
      default: null
    },
    /* The function of parent that must be executed on key enter. */
    enterKeyAction: {
      type: Function,
      default: null
    },
    /* The function of parent that must be executed on select. */
    selectAction: {
      type: Function,
      default: null
    },
    /* The function of parent that must be executed on clear. */
    clearAction: {
      type: Function,
      default: null
    },
    /* Attribute that indicates if the popper must be append to body. */
    popperAppendToBody: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    /* The text of parent that must be updated by ever input letter. */
    text: {
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    editValue: {
      type: String,
      default: ''
    }
  },
  watch: {
    localModel() {
      // If strict is false then it must be update the parent model.
      if (!this.strict) {
        this.$emit(updateModelEvent, this.localModel);
      }
    },

  },
  methods: {
    querySearchAsync(queryString, cb) {
      this.$emit(updateTextEvent, queryString);
      if (queryString && queryString.length >= 2 && queryString.trim()) {
        const data = {
          url: this.url,
          searchString: queryString.trim(),
          requestParams: {
            shipperAccountId: this.shipperAccountId,
            warehouseId: this.warehouseId,
            includeInactive: this.includeInactive,
            includeOnHold: this.includeOnHold
          }
        };
        return autocompleteApiService.getList(data).then((response) => {
          if (response && response.data && response.data.length > 0) {
            response.data.forEach((element) => {
              // The value field its necessary by native component to show in the input.
              element.value = element[this.labelField];
              element.value += element[this.labelFieldLastWithDash] ? (` - ${element[this.labelFieldLastWithDash]}`) : '';
            });
            this.list = response.data;
            cb(response.data);
          } else {
            cb([]);
          }
        });
      }
      return false;
    },
    handleSelect(item) {
      this.$emit(updateModelEvent, item[this.valueField]);
      if (this.labelfieldSelected) {
        this.localModel = item[this.labelfieldSelected];
      }
      if (this.valueFieldAdditional) {
        this.$emit('update:modelAdditional', item[this.valueFieldAdditional]);
      }

      // Excecute parent select action.
      if (this.selectAction) {
        this.selectAction(item);
      }
      // Emit select event for parent listeners
      this.$emit('select', item);
    },
    clearAll() {
      this.$emit(updateModelEvent, null);
      // Excecute parent clearAction action.
      if (this.clearAction) {
        this.clearAction();
      }
    },
    clearParentModel(event) {
      // tab, enter, end, home, left, right
      const isControlKey = constants.KEY_CODE.CONTROL_KEYS_NO_BACKSPACE.find(key => key === event.keyCode);

      if (!isControlKey && this.model && this.strict) {
        this.$emit(updateModelEvent, null);
      }

      if (constants.KEY_CODE.ENTER === event.keyCode && this.enterKeyAction) {
        this.enterKeyAction();
      }
    },
    clearLocalModel() {
      this.localModel = null;
    },
    setLocalModel(val) {
      this.localModel = val;
    },
    getLocalModel() {
      return this.localModel;
    },
    selectWithTab() {
      if (this.list.length && this.$refs[autocompletePrincipalRefStr].highlightedIndex >= 0) {
        const item = this.list[this.$refs[autocompletePrincipalRefStr].highlightedIndex];
        this.$refs[autocompletePrincipalRefStr].select(item);
      }
    }
  }
};
</script>
<style rel="stylesheet/scss" lang="scss">
.autocomplete-popper {
    min-width: 30%;
}
</style>
